import { buttonClasses } from '@mui/material'
import { Theme } from '@mui/material/styles'

import { layout } from '../tokens.generated'

// ----------------------------------------------------------------------
// A group is a row of buttons, not a raised bar. It used to give a contained
// group back the coloured shadow the buttons themselves had lost, unless the
// screen remembered to pass `disableElevation`; now the group is flat by
// default and there is no variant that puts a shadow on it.
//
// Inside a group the fixed control height becomes a minimum and the buttons
// stretch to the tallest of them. The split buttons put a small toggle beside a
// medium action, and with two fixed heights in one group the toggle would sit
// eight pixels short of its neighbour.
// ----------------------------------------------------------------------

declare module '@mui/material/ButtonGroup' {
  // A group hands its `variant` to every button in it, so it accepts exactly
  // the variants a button does. Without this a `ButtonProps` spread onto a
  // group stops compiling the moment the button gains a variant.
  interface ButtonGroupPropsVariantOverrides {
    ink: true
    beastPrimary: true
  }
}

export default function ButtonGroup(theme: Theme) {
  return {
    MuiButtonGroup: {
      defaultProps: {
        disableElevation: true,
      },
      variants: [
        {
          props: { disabled: true },
          style: {
            'boxShadow': 'none',
            '& .MuiButtonGroup-grouped.Mui-disabled': {
              'border': `solid 1px ${theme.palette.action.disabledBackground}`,
              '&.MuiButton-contained': {
                backgroundColor: theme.palette.action.disabledBackground,
              },
            },
          },
        },
      ],

      styleOverrides: {
        root: {
          'boxShadow': 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        grouped: {
          height: 'auto',
          alignSelf: 'stretch',
          [`&.${buttonClasses.sizeSmall}`]: {
            minHeight: layout.controlHeightSmall,
          },
          [`&.${buttonClasses.sizeMedium}`]: {
            minHeight: layout.controlHeight,
          },
          [`&.${buttonClasses.sizeLarge}`]: {
            minHeight: layout.controlHeightLarge,
          },
        },
      },
    },
  }
}
